'use client';
import Link from 'next/link';
import JsonLd from '@/components/JsonLd';
import IndustryCrossLinks from "@/components/landing/IndustryCrossLinks";
import ResearchBacking from "@/components/landing/ResearchBacking";
import Footer from "@/components/shared/Footer";
import PublicHeader from "@/components/shared/PublicHeader";

const restaurantSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Aditya Labs AI Hostess for Restaurants",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description: "AI-powered hostess that answers calls, takes reservations, manages waitlists, and handles enquiries 24/7.",
  url: "https://adityalabs.ai/restaurants",
  audience: { "@type": "BusinessAudience", audienceType: "Restaurants and Hospitality" },
  provider: { "@type": "Organization", name: "Aditya Labs", url: "https://adityalabs.ai" },
  offers: { "@type": "Offer", price: "49", priceCurrency: "USD", description: "Plans from $49/month" },
};

const restaurantFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is an AI hostess for restaurants?",
      acceptedAnswer: { "@type": "Answer", text: "An AI hostess for restaurants is a chatbot trained on your menu, hours, reservation availability, and policies. It answers phone and web enquiries instantly, 24/7, handling reservations, waitlist management, and common questions like dietary options and opening times." },
    },
    {
      "@type": "Question",
      name: "How does AI reservation booking work for restaurants?",
      acceptedAnswer: { "@type": "Answer", text: "The AI uses retrieval-augmented generation (RAG) to answer questions from your uploaded business information — menus, hours, table availability, and policies. When a customer wants to book, the AI checks availability and confirms the reservation. It only shares information you have explicitly provided." },
    },
    {
      "@type": "Question",
      name: "What are the limitations of AI agents for restaurants?",
      acceptedAnswer: { "@type": "Answer", text: "AI agents cannot handle food preparation, provide specific medical dietary advice, or manage real-time kitchen operations. They are designed for structured tasks like taking reservations, answering menu questions, and managing waitlists. Complex requests are always escalated to your staff." },
    },
  ],
};

const restaurantResearch = [
  {
    text: "Restaurants miss up to 30% of incoming phone calls during peak hours, resulting in lost reservations and revenue.",
    source: "Toast Restaurant Technology Report 2024",
    url: "https://pos.toasttab.com/resources/restaurant-success-industry-report",
  },
  {
    text: "The average restaurant loses $1,000–$2,000 per week in missed reservations from unanswered calls alone.",
    source: "National Restaurant Association 2024 State of the Industry",
    url: "https://restaurant.org/research-and-media/research/",
  },
  {
    text: "85% of diners won&apos;t call back if their first call goes unanswered — they&apos;ll book at a competitor instead.",
    source: "OpenTable Diner Behaviour Report 2024",
    url: "https://www.opentable.com/",
  },
];
export default function RestaurantLandingPage() {
  return (
    <div className="min-h-screen bg-[#0B0F19] text-white font-sans overflow-x-hidden selection:bg-orange-500/30">
      <JsonLd data={restaurantSchema} />
      <JsonLd data={restaurantFaqSchema} />

      {/* NAVBAR */}
      <PublicHeader variant="industry" accentColor="orange" maxWidth="max-w-7xl" />
      {/* HERO SECTION */}
      <section className="relative pt-16 pb-24 lg:pt-32 lg:pb-40 overflow-hidden">

        {/* Ambient Background Glow (Warm Orange) */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-orange-600/10 rounded-full blur-[120px] -z-10 opacity-50"></div>
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">

          {/* LEFT COLUMN: COPY */}
          <div className="space-y-10 relative z-10">

            {/* Urgency Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold tracking-wider uppercase">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
              Peak Hours: You missed 12 calls last night
            </div>
            {/* Headline */}
            <h1 className="text-5xl lg:text-7xl font-extrabold leading-tight tracking-tight">
              The Phone Is Ringing. <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">Nobody Can Answer.</span>
            </h1>
            {/* Subheadline */}
            <p className="text-lg lg:text-xl text-gray-400 max-w-lg leading-relaxed">
              Your staff is busy serving food. Our AI Hostess answers every call, takes reservations, manages the waitlist, and answers &ldquo;Are you open?&rdquo; automatically.
            </p>
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link href="/signup" className="flex items-center justify-center bg-orange-600 hover:bg-orange-500 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-xl shadow-orange-900/40 transition-all transform hover:-translate-y-1">
                Deploy AI Hostess
              </Link>
              <Link href="/demo#restaurant" className="flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 text-white px-8 py-4 rounded-xl font-medium backdrop-blur-sm transition-all">
                Listen To Call
              </Link>
            </div>
            {/* Trust/Guarantee */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-6 text-sm text-gray-500 dark:text-gray-400 pt-6 border-t border-gray-800/50">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                <span className="font-medium text-gray-300">Connects to Your Reservation System</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                <span className="font-medium text-gray-300">&ldquo;Zero Missed Calls&rdquo; Guarantee</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 max-w-md">If you miss a single call during business hours while our AI is active, your next month is free. The AI Hostess works alongside your team — not instead of them.</p>
            </div>
          </div>
          {/* RIGHT COLUMN: VISUAL PROOF (Reservation Automation) */}
          <div className="relative mx-auto lg:mx-0 w-full max-w-md">

            {/* Glass Container */}
            <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-3 rounded-[2.5rem] border border-gray-700/50 shadow-2xl backdrop-blur-xl relative z-10">
              <div className="bg-black rounded-[2rem] overflow-hidden border border-gray-800 h-[650px] flex flex-col relative">

                {/* Status Bar */}
                <div className="bg-gray-900/80 backdrop-blur-md p-4 flex items-center justify-between border-b border-gray-800 sticky top-0 z-20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-xs font-bold text-white shadow-lg">AI</div>
                    <div>
                      <div className="font-semibold text-white text-sm">Front of House</div>
                      <div className="text-[10px] text-orange-400 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse"></span>
                        Handling 3 Calls
                      </div>
                    </div>
                  </div>
                </div>
                {/* Chat Area */}
                <div className="flex-1 p-5 space-y-6 overflow-y-auto bg-gray-950 scrollbar-hide">
                  <div className="text-center text-[10px] font-medium text-gray-600 dark:text-gray-400 uppercase tracking-widest my-4">Friday 7:30 PM (Peak Rush)</div>
                  {/* Message 1 (Caller) */}
                  <div className="flex justify-end">
                    <div className="bg-gray-800 text-gray-100 px-4 py-3 rounded-2xl rounded-tr-sm max-w-[85%] text-sm leading-relaxed shadow-md">
                      Hi, do you have a table for 4 tonight around 8?
                    </div>
                  </div>
                  {/* Message 2 (AI) */}
                  <div className="flex justify-start">
                    <div className="bg-orange-600 text-white px-4 py-3 rounded-2xl rounded-tl-sm max-w-[85%] text-sm leading-relaxed shadow-lg shadow-orange-900/20">
                      We are fully booked at 8:00, but I have a table opening up at 8:15 PM or 8:30 PM. Would either of those work? 🍽️
                    </div>
                  </div>
                   {/* Message 3 (Caller) */}
                   <div className="flex justify-end">
                    <div className="bg-gray-800 text-gray-100 px-4 py-3 rounded-2xl rounded-tr-sm max-w-[85%] text-sm leading-relaxed shadow-md">
                      8:15 is perfect.
                    </div>
                  </div>
                  {/* Message 4 (AI Confirmation) */}
                  <div className="flex justify-start">
                    <div className="bg-orange-600 text-white px-4 py-3 rounded-2xl rounded-tl-sm max-w-[85%] text-sm leading-relaxed shadow-lg shadow-orange-900/20">
                      Great. I&apos;ve locked it in for 4 people at 8:15 PM under this phone number. See you then!
                    </div>
                  </div>
                  {/* Reservation Card */}
                  <div className="flex justify-center my-6">
                    <div className="bg-gray-900 border border-gray-700 p-3 rounded-xl w-64 shadow-2xl flex items-center gap-3">
                        <div className="bg-green-500/20 text-green-500 p-2 rounded-lg">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        </div>
                        <div>
                            <div className="text-xs text-gray-400">Reservation Confirmed</div>
                            <div className="text-sm font-bold text-white">Table 4 &bull; 8:15 PM</div>
                        </div>
                    </div>
                  </div>
                </div>
                {/* Input Area (Visual) */}
                <div className="p-4 bg-gray-900 border-t border-gray-800">
                  <div className="h-12 bg-gray-800 rounded-full w-full opacity-50 border border-gray-700"></div>
                </div>
              </div>
            </div>
            {/* Back Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-orange-500/20 blur-[90px] -z-10 rounded-full"></div>
          </div>
        </div>
      </section>
      {/* Social Proof */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">What Restaurant Owners Are Saying</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <div className="flex items-center gap-1 mb-3">
                <span className="text-yellow-400 text-sm">★★★★★</span>
              </div>
              <p className="text-sm text-gray-300 italic leading-relaxed mb-4">
                &quot;Friday and Saturday nights, we were missing 15+ calls during service. The AI Hostess now handles every call. Last month it booked 47 reservations we would have lost.&quot;
              </p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white text-xs font-bold">MR</div>
                <div>
                  <p className="text-sm font-semibold text-white">Marco R.</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Owner, 90-Seat Italian Restaurant, Miami FL</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <div className="flex items-center gap-1 mb-3">
                <span className="text-yellow-400 text-sm">★★★★★</span>
              </div>
              <p className="text-sm text-gray-300 italic leading-relaxed mb-4">
                &quot;Our hostess was trying to seat guests AND answer the phone at the same time. Now she focuses on the dining room while the AI handles calls and web inquiries. Covers went up 12%.&quot;
              </p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center text-white text-xs font-bold">LC</div>
                <div>
                  <p className="text-sm font-semibold text-white">Lisa C.</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">General Manager, Steakhouse, Dallas TX</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-10 px-6">
        <div className="max-w-2xl mx-auto text-center bg-orange-900/20 border border-orange-700/30 rounded-2xl p-8">
          <p className="text-white font-semibold text-lg mb-2">Plans start at $49/month</p>
          <p className="text-gray-400 text-sm mb-4">One extra reservation per week covers your entire subscription.</p>
          <Link href="/pricing" className="text-orange-400 hover:text-orange-300 text-sm font-medium underline">View all pricing plans →</Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 bg-gray-900/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[...restaurantFaqSchema.mainEntity,
              { name: "Does the AI Hostess replace my staff?", acceptedAnswer: { text: "No — the AI Hostess works alongside your team, not instead of them. It handles phone calls and web inquiries so your staff can focus on in-person guest experience. Think of it as an extra team member that never takes a break during peak hours." } },
              { name: "How does the AI handle phone calls?", acceptedAnswer: { text: "The AI Hostess works through two channels: a website chat widget that handles web inquiries 24/7, and an AI voice agent that answers phone calls with a natural-sounding voice. The voice agent picks up calls your staff can't answer during peak hours, takes reservation details, answers questions about hours and menu, and texts a confirmation to the caller. Setup takes minutes — you forward your overflow calls to our AI number." } },
              { name: "Does it work with my POS or reservation system?", acceptedAnswer: { text: "The AI sends structured reservation details (name, party size, date, time, dietary needs) that your team can enter into any system — Toast, Resy, Yelp Reservations, or a paper book. For Toast and Resy specifically, we offer webhook integration on Business plans and above so reservations sync automatically." } },
            ].map((faq: { name: string; acceptedAnswer: { text: string } }, i: number) => (
              <div key={i} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-3">{faq.name}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{faq.acceptedAnswer.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ResearchBacking stats={restaurantResearch} />
      <IndustryCrossLinks current="restaurants" />
      <Footer />
    </div>
  );
}
